import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTextField } from '@react-aria/textfield';
import { forwardRef, Fragment, useEffect, useMemo, useRef } from 'react';
import { isArray, isBoolean, isFormValidationErrorDisplayed, isString } from '../../common.types.js';
import { useDimensionsRef } from '../../hooks/useDimensionsRef/index.js';
import { BKJIcon } from '../BKJIcon/index.js';
import { mergeRefs } from '../BKJSelect/utils/mergeRefs.js';
import { BKJTextInputDescription, BKJTextInputErrorMessage, BKJTextInputFieldSet, BKJTextInputIconWrapper, BKJTextInputLabel, BKJTextInputRequiredAsterisk, BKJTextInputTextInput, BKJTextInputWrapper, } from './BKJTextInput.styled.js';
import i18next from 'i18next';
export const BKJTextInput = forwardRef((props, ref) => {
    const { overrides = [], variant = 'RoundEdges', leftIconProps, rightIconProps, labelProps: __labelProps__, fieldSetProps = {}, name, className, required: isRequired, disabled: isDisabled, label, value, autoFocus, description, error, onChange, onBeforeInput, onBlur, onCompositionEnd, onCompositionStart, onCompositionUpdate, onCopy, onCut, onFocus, onInput, onKeyDown, onKeyUp, onPaste, onSelect, autoComplete = 'off', } = props;
    let innerRef = useRef({});
    const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField({
        isRequired,
        isDisabled,
        autoComplete,
        label: label || `Enter a ${name}`,
        value,
        autoFocus,
        description,
        errorMessage: error,
        onBeforeInput,
        onBlur: onBlur,
        onChange: onChange,
        onCompositionEnd,
        onCompositionStart,
        onCompositionUpdate,
        onCopy,
        onCut,
        onFocus: onFocus,
        onInput,
        onKeyDown,
        onKeyUp,
        onPaste,
        onSelect,
    }, innerRef);
    const [leftIconRef, leftDimm] = useDimensionsRef();
    const [rightIconRef, rightDimm] = useDimensionsRef();
    const rightIconFontSize = useMemo(() => (!!rightIconProps ? rightIconProps.fontSize || 24 : 0), [rightIconProps]);
    const leftIconFontSize = useMemo(() => (!!leftIconProps ? leftIconProps.fontSize || 24 : 0), [leftIconProps]);
    const getInputCSS = useMemo(() => {
        return {
            paddingRight: `${rightDimm.width + (!!rightIconFontSize ? rightIconFontSize / 2 : 12)}px`,
            paddingLeft: `${leftDimm.width + (!!leftIconFontSize ? leftIconFontSize / 2 : 12)}px`,
        };
    }, [leftDimm.width, leftIconFontSize, rightDimm.width, rightIconFontSize]);
    useEffect(() => {
        if (isFormValidationErrorDisplayed(error)) {
            let errorMessage = '';
            if (isArray(error) && error.every((msg) => isString(msg) && msg !== '')) {
                errorMessage = error.join('\n');
            }
            if (isString(error))
                errorMessage = error;
            if (isBoolean(error))
                errorMessage = 'Error';
            if (isArray(error) && error.length === 0)
                errorMessage = '';
            innerRef.current?.setCustomValidity(errorMessage);
        }
        else {
            innerRef.current?.setCustomValidity('');
        }
    }, [error]);
    const restInputProps = { ...props };
    delete restInputProps.className;
    delete restInputProps.children;
    delete restInputProps.css;
    return (_jsxs(BKJTextInputFieldSet, { ...fieldSetProps, className: className, children: [props.label && (_jsxs(BKJTextInputLabel, { ...__labelProps__, ...labelProps, variant: variant, elementType: "Label", children: [props.label, props.required && _jsx(BKJTextInputRequiredAsterisk, { children: "\u00A0*" })] })), _jsxs(BKJTextInputWrapper, { htmlFor: labelProps.htmlFor, variant: variant, disabled: props.disabled, elementType: "InputWrapper", isOpen: props.isOpen, children: [!!leftIconProps && (_jsx(BKJTextInputIconWrapper, { ...leftIconProps, padding: `0 0 0 ${leftIconFontSize / 3}px`, style: {
                            left: 0,
                            cursor: `${!!leftIconProps?.onClick ? 'pointer' : 'default'}`,
                        }, onClick: leftIconProps?.onClick, ref: leftIconRef, css: leftIconProps?.css, className: leftIconProps?.className, children: _jsx(BKJIcon, { iconName: leftIconProps?.iconName, fontSize: leftIconProps.fontSize }) })), _createElement(BKJTextInputTextInput, { ...inputProps, ...restInputProps, onChange: props.onChange, value: props.value, name: props.name, key: name, required: overrides.includes('NO_REQUIRED') ? undefined : props.required, onInvalid: () => {
                            innerRef?.current?.setCustomValidity(i18next.t('translation:PLEASE_FILL_OUT_THIS_FIELD'));
                        }, error: error, ref: mergeRefs([innerRef, ref]), variant: variant, elementType: "Input", style: { ...getInputCSS }, autoComplete: autoComplete, hasLeftIcon: !!leftIconProps, hasRightIcon: !!rightIconProps }), !!rightIconProps && (_jsx(BKJTextInputIconWrapper, { ...rightIconProps, padding: `0 ${rightIconFontSize / 3}px 0 0`, style: {
                            right: 0,
                            cursor: `${!!rightIconProps?.onClick ? 'pointer' : 'default'}`,
                        }, ref: rightIconRef, onClick: rightIconProps?.onClick, css: rightIconProps?.css, className: rightIconProps?.className, children: _jsx(BKJIcon, { iconName: rightIconProps.iconName, fontSize: rightIconProps?.fontSize }) }))] }), props.description && (_jsx(BKJTextInputDescription, { ...descriptionProps, children: props.description })), isFormValidationErrorDisplayed(error) && (_jsx(Fragment, { children: error && Array.isArray(error) ? (error.length &&
                    error.map((err, i) => (_jsx(BKJTextInputErrorMessage, { ...errorMessageProps, children: err }, `${err}-${i}`)))) : (_jsx(BKJTextInputErrorMessage, { ...errorMessageProps, children: error })) }))] }));
});
//# sourceMappingURL=BKJTextInput.js.map